import { isDefined } from '@sgme/fp';
import { DocumentError } from 'apiConnector/document/document';
import { DocumentationV3, DocumentItem, PublicDocumentItem } from 'epic/common-form/Documentation';
import { useBlotterPanelContext } from 'hooks/useBlotterPanelContext';
import { useDownloadDlpDocument } from 'hooks/useDownloadDlpDocument';
import { useDownloadTermsheet } from 'hooks/useDownloadTermsheet';
import { useTranslation } from 'hooks/useTranslation';
import { isEmpty } from 'lodash-es';
import { IBlotterItem } from 'models/IBlotterItem';
import { useCallback, useMemo } from 'react';

import { getDocumentOfType } from './utils';

export const GenerationFailedError = {
  type: 'TECHNICAL',
} satisfies DocumentError;

export const toEndUserError = (error: string | null | undefined): DocumentError | null => {
  if (error) {
    return {
      type: 'END_USER_ERROR',
      errorMessages: [error],
    };
  }
  return null;
};

export function useTermsheetDownload(blotterItem: IBlotterItem) {
  const downloadTermsheetErrorMessage = useTranslation('downloadTermsheet.error');

  const { isDownloading: isDownloadingTermsheet, downloadTermsheet, error: errorTermsheet } = useDownloadTermsheet();

  const {
    isDownloading: isDownloadingDlpDocument,
    downloadDlpDocument,
    error: errorDlp,
  } = useDownloadDlpDocument(downloadTermsheetErrorMessage);

  const onTermsheetDownload = useCallback(
    (doc: DocumentItem) => {
      if (doc.public === false) {
        downloadDlpDocument(doc.documentToken!);
      } else {
        if (isDefined((doc as PublicDocumentItem).url)) {
          return;
        }
        downloadTermsheet(blotterItem.productId, blotterItem, doc.language);
      }
    },
    [blotterItem, downloadTermsheet, downloadDlpDocument],
  );

  const hasError = errorTermsheet !== null || errorDlp !== null;

  return {
    onTermsheetDownload,
    isTermsheetDownloading: isDownloadingTermsheet || isDownloadingDlpDocument,
    error: hasError
      ? (errorTermsheet ??
        ({
          type: 'END_USER_ERROR',
          errorMessages: [errorDlp!],
        } satisfies DocumentError))
      : undefined,
  };
}

export function useDocumentItems(documents: DocumentationV3, blotterItem: IBlotterItem) {
  const brochureItems = useMemo(() => getDocumentOfType(documents, 'Brochure'), [documents]);
  const preConfirmationItems = useMemo(
    () => getDocumentOfType(documents, 'PreConfirmation', blotterItem),
    [documents, blotterItem],
  );
  const termSheetItems = useMemo(
    () => getDocumentOfType(documents, 'TermSheet', blotterItem),
    [documents, blotterItem],
  );
  const kidItems = useMemo(() => getDocumentOfType(documents, 'Kid'), [documents]);
  const pgItems = useMemo(() => getDocumentOfType(documents, 'ProductGovernance'), [documents]);
  const marketingMaterialItems = useMemo(
    () => getDocumentOfType(documents, 'MarketingMaterial', blotterItem),
    [documents, blotterItem],
  );
  const finalTermsItems = useMemo(
    () => getDocumentOfType(documents, 'FinalTerms', blotterItem),
    [documents, blotterItem],
  );

  const count = [
    brochureItems,
    preConfirmationItems,
    termSheetItems,
    kidItems,
    pgItems,
    marketingMaterialItems,
    finalTermsItems,
  ].filter(items => !isEmpty(items)).length;

  if (count === 0) {
    return {
      brochureItems: [],
      preConfirmationItems: [],
      termSheetItems: [],
      kidItems: [],
      pgItems: [],
      marketingMaterialItems: [],
      finalTermsItems: [],
      count: 0,
    };
  }
  return {
    brochureItems,
    preConfirmationItems,
    termSheetItems,
    kidItems,
    pgItems,
    marketingMaterialItems,
    finalTermsItems,
    count,
  };
}

export function useAnalyticsCategory() {
  const { blotterType } = useBlotterPanelContext()!;

  return useMemo(() => {
    switch (blotterType) {
      case 'quotes':
        return 'Blotter My Quotes';
      case 'trades':
        return 'Blotter My Trades';
      case 'products':
        return 'Blotter My Products';
      case 'dlpProducts':
        return 'Blotter My flow products';
      default:
        return undefined;
    }
  }, [blotterType]);
}
